import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { apiErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import Layout from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { KeyRound, Loader2, Save, Eye, EyeOff, ShieldCheck, PhoneCall, Send, MessageCircle, Video, Sparkles, AudioLines, School, Mic } from "lucide-react";
import { toast } from "sonner";
import DevAdminVoiceConfig from "@/pages/DevAdminVoiceConfig";
import DevAdminTutorVoices from "@/pages/DevAdminTutorVoices";

const GROUP_ICON = { whatsapp: MessageCircle, zoho: Video, ai: Sparkles, speech: AudioLines, school: School };

export default function DevAdminConfig() {
  const { user } = useAuth();
  const navigate = useNavigate();

  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [edits, setEdits] = useState({});      // key -> new value
  const [reveal, setReveal] = useState({});
  const [testPhone, setTestPhone] = useState("");
  const [testing, setTesting] = useState(false);
  const [tab, setTab] = useState("");

  useEffect(() => {
    if (!tab && groups.length) setTab(groups[0].group);
  }, [groups, tab]);

  const sendWhatsAppTest = async () => {
    if (!testPhone.trim()) { toast.error("Enter a phone number (e.g. 919876543210)"); return; }
    setTesting(true);
    try {
      const { data } = await api.post("/devadmin/whatsapp/test", { phone: testPhone.trim() });
      if (data.success) {
        toast.success(`Sent via ${data.provider}: ${data.detail || "OK"}`, { duration: 7000 });
      } else {
        toast.error(`Failed via ${data.provider} [${data.error_code || "error"}]: ${data.detail || "Unknown error"}`, { duration: 9000 });
      }
    } catch (e) {
      toast.error(apiErr(e.response?.data?.detail) || "Test failed");
    } finally { setTesting(false); }
  };

  useEffect(() => {
    if (user && !user.is_dev_admin) navigate("/dashboard", { replace: true });
  }, [user, navigate]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/devadmin/config");
      setGroups(data.groups || []);
      setEdits({});
    } catch (e) {
      toast.error(apiErr(e.response?.data?.detail) || "Failed to load config");
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const setVal = (key, val) => setEdits((p) => ({ ...p, [key]: val }));

  const save = async () => {
    const values = {};
    for (const g of groups) {
      for (const it of g.items) {
        if (it.key in edits) {
          // For secrets, only send when the user typed something new.
          if (it.secret) { if (edits[it.key] && edits[it.key].trim()) values[it.key] = edits[it.key].trim(); }
          else values[it.key] = edits[it.key];
        }
      }
    }
    if (Object.keys(values).length === 0) { toast.info("No changes to save"); return; }
    setSaving(true);
    try {
      const { data } = await api.put("/devadmin/config", { values });
      setGroups(data.groups || []);
      setEdits({});
      toast.success("Configuration saved & applied");
    } catch (e) {
      toast.error(apiErr(e.response?.data?.detail) || "Could not save");
    } finally { setSaving(false); }
  };

  if (loading) {
    return <Layout><div className="py-24 text-center text-slate-400"><Loader2 className="h-6 w-6 mx-auto animate-spin" /></div></Layout>;
  }

  return (
    <Layout>
      <div className="space-y-6 max-w-4xl" data-testid="devadmin-config-page">
        <div>
          <h1 className="text-3xl font-bold text-slate-900 dark:text-slate-100 flex items-center gap-2" style={{ fontFamily: "Outfit" }}>
            <KeyRound className="h-7 w-7 text-amber-500" /> Settings
          </h1>
          <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">
            Manage all integration keys and settings here. Saved values override <code>.env</code> and apply instantly — no redeploy needed.
          </p>
        </div>

        <Tabs value={tab} onValueChange={setTab} className="w-full">
          <TabsList className="h-auto flex-wrap gap-1 bg-slate-100 dark:bg-slate-800/60" data-testid="settings-tabs">
            {groups.map((g) => {
              const Icon = GROUP_ICON[g.group] || KeyRound;
              return (
                <TabsTrigger key={g.group} value={g.group} data-testid={`settings-tab-${g.group}`} className="gap-1.5">
                  <Icon className="h-4 w-4" /> {g.label}
                </TabsTrigger>
              );
            })}
            <TabsTrigger value="__voice" data-testid="settings-tab-voice" className="gap-1.5">
              <PhoneCall className="h-4 w-4" /> Voice Agents
            </TabsTrigger>
            <TabsTrigger value="__tutorvoices" data-testid="settings-tab-tutorvoices" className="gap-1.5">
              <Mic className="h-4 w-4" /> Tutor Voices
            </TabsTrigger>
          </TabsList>

          {groups.map((g) => (
            <TabsContent key={g.group} value={g.group} className="mt-6">
              <div className="flex items-center justify-end mb-4">
                <Button onClick={save} disabled={saving} className="rounded-full bg-amber-500 hover:bg-amber-600 text-white" data-testid="config-save-btn">
                  {saving ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Save className="h-4 w-4 mr-1.5" />} Save changes
                </Button>
              </div>
              <div className="rounded-2xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 p-5" data-testid={`config-group-${g.group}`}>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {g.items.map((it) => {
                    const editing = it.key in edits;
                    const display = editing ? edits[it.key] : (it.secret ? "" : it.value);
                    return (
                      <div key={it.key}>
                        <label className="flex items-center gap-2 text-[10px] font-semibold uppercase tracking-wider text-slate-400 mb-1">
                          {it.label}
                          {it.source === "env" && <span className="text-[9px] px-1 py-0.5 rounded bg-slate-100 dark:bg-slate-700 text-slate-500 normal-case tracking-normal">from env</span>}
                          {it.source === "db" && <span className="text-[9px] px-1 py-0.5 rounded bg-emerald-100 dark:bg-emerald-900/40 text-emerald-600 dark:text-emerald-300 normal-case tracking-normal">saved</span>}
                        </label>
                        {it.secret ? (
                          <div className="relative">
                            <Input
                              type={reveal[it.key] ? "text" : "password"}
                              value={display}
                              onChange={(e) => setVal(it.key, e.target.value)}
                              placeholder={it.has_value ? `${it.value} — leave blank to keep` : (it.placeholder || "Not set")}
                              className="rounded-xl pr-9"
                              data-testid={`config-field-${it.key}`}
                            />
                            <button type="button" onClick={() => setReveal((r) => ({ ...r, [it.key]: !r[it.key] }))} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600" tabIndex={-1}>
                              {reveal[it.key] ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                            </button>
                          </div>
                        ) : it.options ? (
                          <Select value={(editing ? edits[it.key] : it.value) || ""} onValueChange={(v) => setVal(it.key, v)}>
                            <SelectTrigger className="rounded-xl" data-testid={`config-field-${it.key}`}>
                              <SelectValue placeholder={it.placeholder || "Select"} />
                            </SelectTrigger>
                            <SelectContent>
                              {it.options.map((o) => (
                                <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        ) : (
                          <Input value={display} onChange={(e) => setVal(it.key, e.target.value)} placeholder={it.placeholder || ""} className="rounded-xl" data-testid={`config-field-${it.key}`} />
                        )}
                        {it.help && <p className="text-[11px] text-slate-400 mt-1">{it.help}</p>}
                      </div>
                    );
                  })}
                </div>
                {g.group === "whatsapp" && (
                  <div className="mt-5 pt-4 border-t border-slate-100 dark:border-slate-800">
                    <div className="flex items-center gap-2 text-sm font-semibold text-slate-800 dark:text-slate-100 mb-1">
                      <Send className="h-4 w-4 text-emerald-500" /> Send a test message
                    </div>
                    <p className="text-[11px] text-slate-400 mb-2">
                      Sends a sample report-card message to the number below via the <b>active</b> provider — verify your campaign/template before publishing to real parents. Save any changes above first.
                    </p>
                    <div className="flex items-center gap-2 flex-wrap">
                      <Input
                        value={testPhone}
                        onChange={(e) => setTestPhone(e.target.value)}
                        placeholder="Phone with country code, e.g. 919876543210"
                        className="rounded-xl max-w-xs"
                        data-testid="wa-test-phone"
                      />
                      <Button onClick={sendWhatsAppTest} disabled={testing} className="rounded-full bg-emerald-600 hover:bg-emerald-700" data-testid="wa-test-send">
                        {testing ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Send className="h-4 w-4 mr-1.5" />} Send test
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            </TabsContent>
          ))}

          <TabsContent value="__voice" className="mt-6">
            <DevAdminVoiceConfig embedded />
          </TabsContent>

          <TabsContent value="__tutorvoices" className="mt-6">
            <DevAdminTutorVoices />
          </TabsContent>
        </Tabs>
      </div>
    </Layout>
  );
}
